import { useState, useEffect } from "react";
import { useAnnouncements } from "@/hooks/use-announcements";
import { useTodaysBirthdays } from "@/hooks/use-birthdays";
import { TVFooter } from "@/components/display/TVFooter";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
}

export default function DisplayKantinePage() {
  const { data: announcements } = useAnnouncements();
  const { data: birthdays } = useTodaysBirthdays();

  const [announcementIdx, setAnnouncementIdx] = useState(0);

  // Auto-rotate announcements every 6 seconds
  useEffect(() => {
    if (!announcements || announcements.length <= 1) return;
    const timer = setInterval(() => {
      setAnnouncementIdx((prev) => (prev + 1) % announcements.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [announcements]);

  const current = announcements?.[announcementIdx];

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <iframe
          src="https://player.vimeo.com/video/1126590090?background=1&autoplay=1&loop=1&byline=0&title=0&muted=1"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      </div>

      {/* Subtle overlay */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Content */}
      <div className="relative z-10 min-h-screen p-8 2xl:p-12 flex flex-col">
        <div className="flex-1" />

        {/* Info Cards */}
        <div className="grid gap-6 2xl:gap-8 lg:grid-cols-3 mb-6 2xl:mb-8">
          {/* Fra ledelsen */}
          <div className="rounded-xl bg-black/40 backdrop-blur-sm border border-white/10 p-6 2xl:p-8">
            <div className="flex items-center justify-between mb-4 2xl:mb-6">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-blue-500/30 p-2 2xl:p-3">
                  <SrIcon name="megaphone" className="h-5 w-5 2xl:h-6 2xl:w-6 text-blue-400"  />
                </div>
                <h2 className="text-lg 2xl:text-xl font-semibold text-white">Fra ledelsen</h2>
              </div>
              {announcements && announcements.length > 1 && (
                <div className="flex gap-1.5">
                  {announcements.map((_, index) => (
                    <div
                      key={index}
                      className={cn(
                        "h-2 2xl:h-2.5 rounded-full transition-all duration-300",
                        index === announcementIdx
                          ? "w-6 2xl:w-8 bg-blue-400"
                          : "w-2 2xl:w-2.5 bg-white/30"
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="relative h-20 2xl:h-24 overflow-hidden">
              {current ? (
                <div key={announcementIdx} className="animate-fade-in">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base 2xl:text-lg font-medium text-white/90">{current.title}</h3>
                    <span className="text-sm 2xl:text-base text-white/50">{formatShortDate(current.published_at)}</span>
                  </div>
                  <p className="text-sm 2xl:text-base text-white/70 line-clamp-3 mt-2">{current.message}</p>
                </div>
              ) : (
                <p className="text-base text-white/50">Ingen meldinger</p>
              )}
            </div>
          </div>

          {/* Kommende hendelser (placeholder, kan kobles til Google Calendar API seinere) */}
          <div className="rounded-xl bg-black/40 backdrop-blur-sm border border-white/10 p-6 2xl:p-8">
            <div className="flex items-center gap-3 mb-4 2xl:mb-6">
              <div className="rounded-full bg-emerald-500/30 p-2 2xl:p-3">
                <SrIcon name="calendar" className="h-5 w-5 2xl:h-6 2xl:w-6 text-emerald-400"  />
              </div>
              <h2 className="text-lg 2xl:text-xl font-semibold text-white">Kommende hendelser</h2>
            </div>
            <div className="h-20 2xl:h-24 flex items-center">
              <p className="text-base 2xl:text-lg text-white/50">Kobles til Google Calendar</p>
            </div>
          </div>

          {/* Dagens bursdager */}
          <div className="rounded-xl bg-black/40 backdrop-blur-sm border border-white/10 p-6 2xl:p-8">
            <div className="flex items-center gap-3 mb-4 2xl:mb-6">
              <div className="rounded-full bg-pink-500/30 p-2 2xl:p-3">
                <SrIcon name="cake" className="h-5 w-5 2xl:h-6 2xl:w-6 text-pink-400"  />
              </div>
              <h2 className="text-lg 2xl:text-xl font-semibold text-white">Dagens bursdager</h2>
            </div>
            {birthdays && birthdays.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {birthdays.map((person) => {
                  const initials = person.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2);
                  return (
                    <div key={person.id} className="flex items-center gap-3 rounded-full bg-pink-500/20 ring-1 ring-pink-400 px-4 py-2">
                      {person.avatar_url ? (
                        <img src={person.avatar_url} alt={person.full_name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-pink-500/30 text-pink-300 text-xs font-medium flex items-center justify-center">{initials}</span>
                      )}
                      <div>
                        <h3 className="text-base 2xl:text-lg font-medium text-white">{person.full_name}</h3>
                        <span className="text-sm 2xl:text-base text-pink-300">Gratulerer!</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-base 2xl:text-lg text-white/50">Ingen bursdager i dag</p>
            )}
          </div>
        </div>

        <TVFooter variant="dark" />
      </div>
    </div>
  );
}
